const mobileMediaQuery = window.matchMedia('(max-width: 899px)');
const reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const modalTransitionFallbackMs = 1000;
const iframeLoadFallbackMs = 5000;

let modalBound = false;
let previousActiveElement = null;
let modalState = 'closed';
let closeTimeoutId = 0;
let iframeLoadTimeoutId = 0;

function getModalElements() {
  const modalEl = document.querySelector('[data-teaser-modal]');
  const iframeEl = modalEl ? modalEl.querySelector('iframe') : null;
  const closeButtonEl = modalEl ? modalEl.querySelector('.teaser-modal__close') : null;

  return {
    modalEl,
    iframeEl,
    closeButtonEl,
  };
}

function setModalState(modalEl, state) {
  modalState = state;

  if (modalEl) {
    modalEl.dataset.state = state;
    modalEl.classList.remove(
      'teaser-modal--closed',
      'teaser-modal--opening',
      'teaser-modal--loading',
      'teaser-modal--ready',
      'teaser-modal--closing'
    );
    modalEl.classList.add(`teaser-modal--${state}`);
  }
}

function clearCloseTimeout() {
  if (!closeTimeoutId) {
    return;
  }

  window.clearTimeout(closeTimeoutId);
  closeTimeoutId = 0;
}

function clearIframeLoadTimeout() {
  if (!iframeLoadTimeoutId) {
    return;
  }

  window.clearTimeout(iframeLoadTimeoutId);
  iframeLoadTimeoutId = 0;
}

function getOverlayDurationMs() {
  return reducedMotionMediaQuery.matches ? 1 : modalTransitionFallbackMs;
}

function markTeaserReady() {
  const { modalEl } = getModalElements();

  if (!modalEl || modalState === 'closing' || modalState === 'closed') {
    return;
  }

  clearIframeLoadTimeout();
  setModalState(modalEl, 'ready');
}

function finishClosing() {
  const { modalEl, iframeEl } = getModalElements();

  if (modalState !== 'closing') {
    clearCloseTimeout();
    return;
  }

  clearCloseTimeout();
  clearIframeLoadTimeout();

  if (!modalEl || !iframeEl) {
    modalState = 'closed';
    return;
  }

  iframeEl.src = '';
  modalEl.hidden = true;
  setModalState(modalEl, 'closed');
  document.body.classList.remove('teaser-modal-open');

  if (previousActiveElement instanceof HTMLElement) {
    previousActiveElement.focus({ preventScroll: true });
  }

  previousActiveElement = null;
}

function buildEmbedSources(trigger) {
  const sources = [trigger.dataset.teaserEmbedPrimarySrc, trigger.dataset.teaserEmbedFallbackSrc]
    .filter((value, index, array) => typeof value === 'string' && value.trim() !== '' && array.indexOf(value) === index);

  return sources;
}

function buildTriggerSources() {
  const trigger = document.querySelector('[data-teaser-trigger]');

  if (!trigger) {
    return [];
  }

  const videoId = trigger.dataset.teaserYoutubeId;

  if (!videoId) {
    return [];
  }

  return [
    `https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1&controls=1&fs=0&iv_load_policy=3`,
    `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1&controls=1&fs=0&iv_load_policy=3`,
  ];
}

function getSourcesFromEventTarget(target) {
  if (!(target instanceof Element)) {
    return buildTriggerSources();
  }

  const trigger = target.closest('[data-teaser-trigger]');

  if (!trigger) {
    return buildTriggerSources();
  }

  return buildEmbedSources(trigger);
}

function openTeaserModal(target) {
  const { modalEl, iframeEl, closeButtonEl } = getModalElements();
  const sources = getSourcesFromEventTarget(target);

  if (!modalEl || !iframeEl || !closeButtonEl || sources.length === 0 || modalState !== 'closed') {
    return;
  }

  clearCloseTimeout();
  clearIframeLoadTimeout();
  previousActiveElement = target instanceof HTMLElement ? target : null;

  modalEl.hidden = false;
  document.body.classList.add('teaser-modal-open');
  setModalState(modalEl, 'opening');

  modalEl.offsetHeight;

  requestAnimationFrame(() => {
    if (modalState === 'opening') {
      setModalState(modalEl, 'loading');
    }
  });

  iframeEl.src = sources[0];
  iframeLoadTimeoutId = window.setTimeout(markTeaserReady, iframeLoadFallbackMs);

  window.setTimeout(() => {
    closeButtonEl.focus({ preventScroll: true });
  }, 0);
}

function closeTeaserModal() {
  const { modalEl } = getModalElements();

  if (!modalEl || modalState === 'closed' || modalState === 'closing') {
    return;
  }

  clearIframeLoadTimeout();
  setModalState(modalEl, 'closing');
  closeTimeoutId = window.setTimeout(finishClosing, getOverlayDurationMs());
}

function handleTriggerClick(event) {
  event.preventDefault();

  if (mobileMediaQuery.matches) {
    const trigger = event.currentTarget;

    if (trigger instanceof HTMLElement) {
      trigger.blur();
    }
  }

  openTeaserModal(event.currentTarget);
}

function handleModalClick(event) {
  const target = event.target;

  if (!(target instanceof Element) || !target.closest('[data-teaser-close]')) {
    return;
  }

  event.preventDefault();
  closeTeaserModal();
}

function handleEscape(event) {
  if (event.key !== 'Escape' || !document.body.classList.contains('teaser-modal-open')) {
    return;
  }

  closeTeaserModal();
}

function handleIframeLoad() {
  markTeaserReady();
}

function handleModalTransitionEnd(event) {
  const { modalEl } = getModalElements();

  if (!modalEl || event.target !== modalEl || event.propertyName !== 'opacity') {
    return;
  }

  if (modalState === 'closing') {
    finishClosing();
  }
}

export function initTeasers() {
  if (modalBound) {
    return;
  }

  document.querySelectorAll('[data-teaser-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', handleTriggerClick);
  });

  document.querySelectorAll('[data-teaser-close]').forEach((closeControl) => {
    closeControl.addEventListener('click', (event) => {
      event.preventDefault();
      closeTeaserModal();
    });
  });

  const { modalEl, iframeEl } = getModalElements();

  if (modalEl) {
    modalEl.addEventListener('click', handleModalClick);
    modalEl.addEventListener('transitionend', handleModalTransitionEnd);
  }

  if (iframeEl) {
    iframeEl.addEventListener('load', handleIframeLoad);
  }

  document.addEventListener('keydown', handleEscape);
  modalBound = true;
}
